import React from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  Chip,
  Button,
} from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import Loading from '../components/loading';
import ErrorMessage from '../components/errorMessage';
import { useNotificationsInbox } from './hooks/useNotificationsInbox';
import { getNotificationConfig, linkify } from './notificationConfig';

export default function ClinicNotificationsWeb() {
  const {
    notifications,
    isLoading,
    isMarking,
    selected,
    setSelected,
    markAsRead,
    showErrorMessage,
    setShowErrorMessage,
    errorMessage,
  } = useNotificationsInbox({ autoSelectFirst: true });

  return (
    <>
      <Loading isToHidden={!isLoading && !isMarking} />
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardHeader
          title={
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              Minhas Notificações
            </Typography>
          }
        />
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ display: 'flex', minHeight: 500 }}>
            {/* Lista — lado esquerdo */}
            <Box
              sx={{
                width: 320,
                flexShrink: 0,
                borderRight: 1,
                borderColor: 'divider',
                overflowY: 'auto',
              }}
            >
              {notifications.length === 0 && !isLoading ? (
                <Box sx={{ p: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Nenhuma notificação encontrada.
                  </Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {notifications.map((notification, index) => {
                    const config = getNotificationConfig(notification);
                    const isSelected = selected?.id === notification.id;

                    return (
                      <React.Fragment key={notification.id}>
                        <ListItemButton
                          alignItems="flex-start"
                          selected={isSelected}
                          onClick={() => setSelected(notification)}
                          sx={{ py: 1.5, px: 2 }}
                        >
                          <ListItemAvatar sx={{ minWidth: 40 }}>
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                bgcolor: 'transparent',
                                color: config.color,
                              }}
                            >
                              {config.icon}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography
                                  variant="body2"
                                  fontWeight={!notification.read ? 'bold' : 'normal'}
                                  noWrap
                                >
                                  {notification.title}
                                </Typography>
                                <Chip
                                  label={!notification.read ? 'Não lida' : 'Lida'}
                                  color={!notification.read ? 'primary' : 'default'}
                                  size="small"
                                  sx={{ flexShrink: 0 }}
                                />
                              </Box>
                            }
                            secondary={
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                noWrap
                                display="block"
                              >
                                {notification.description?.length > 50
                                  ? `${notification.description.slice(0, 50)}...`
                                  : notification.description}
                              </Typography>
                            }
                          />
                        </ListItemButton>
                        {index < notifications.length - 1 && <Divider component="li" />}
                      </React.Fragment>
                    );
                  })}
                </List>
              )}
            </Box>

            {/* Conteúdo — lado direito */}
            <Box sx={{ flex: 1, p: 3 }}>
              {selected ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <Typography variant="h6" fontWeight="bold" sx={{ flex: 1 }}>
                      {selected.title}
                    </Typography>
                    <Chip
                      label={!selected.read ? 'Não lida' : 'Lida'}
                      color={!selected.read ? 'primary' : 'default'}
                      size="small"
                    />
                    {!selected.read && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<DoneIcon />}
                        onClick={() => markAsRead(selected.id)}
                      >
                        Marcar como lida
                      </Button>
                    )}
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-wrap' }}
                    dangerouslySetInnerHTML={{ __html: linkify(selected.description ?? '') }}
                  />
                </>
              ) : (
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography color="text.secondary" variant="body2">
                    Selecione uma notificação para visualizar.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </>
  );
}
